// src/app/components/charts/LivePriceChart.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";

function fmtTime(ms) {
  try {
    return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function toBinanceSymbol(marketId) {
  // Supports "BTCUSDT", "BTC/USDT", "BTC-USDT"
  return String(marketId || "").replace("/", "").replace("-", "").toUpperCase();
}

function isValidInterval(i) {
  const allowed = new Set(["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"]);
  return allowed.has(String(i || "").toLowerCase());
}

export default function LivePriceChart({
  marketId = "BTCUSDT",
  interval = "1m",
  height, // optional; parent can control container height
}) {
  const symbol = useMemo(() => toBinanceSymbol(marketId), [marketId]);
  const safeInterval = useMemo(() => (isValidInterval(interval) ? interval : "1m"), [interval]);

  const [data, setData] = useState([]);
  const [status, setStatus] = useState({ ok: true, msg: "" });

  const wsRef = useRef(null);

  // REST bootstrap
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus({ ok: true, msg: "Loading…" });
      try {
        const url = `https://api.binance.com/api/v3/klines?symbol=${encodeURIComponent(
          symbol
        )}&interval=${encodeURIComponent(safeInterval)}&limit=400`;

        const res = await fetch(url);
        if (!res.ok) throw new Error(`Market data unavailable (${res.status})`);

        const rows = await res.json();
        const mapped = (Array.isArray(rows) ? rows : []).map((k) => ({
          t: Number(k[0]),
          open: Number(k[1]),
          high: Number(k[2]),
          low: Number(k[3]),
          close: Number(k[4]),
          volume: Number(k[5]),
        }));

        if (cancelled) return;
        setData(mapped);
        setStatus({ ok: true, msg: "" });
      } catch (e) {
        if (cancelled) return;
        setStatus({ ok: false, msg: e?.message || "Failed to load market data" });
        setData([]);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, safeInterval]);

  // WebSocket live updates (kline stream)
  useEffect(() => {
    // Close prior
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }

    const stream = `${symbol.toLowerCase()}@kline_${safeInterval}`;
    const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${stream}`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        const k = msg?.k;
        if (!k) return;

        const bar = {
          t: Number(k.t),
          open: Number(k.o),
          high: Number(k.h),
          low: Number(k.l),
          close: Number(k.c),
          volume: Number(k.v),
        };

        setData((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          if (arr.length === 0) return [bar];

          const last = arr[arr.length - 1];
          if (!last) return [bar];

          // same candle time -> replace last
          if (Number(last.t) === Number(bar.t)) {
            return arr.slice(0, -1).concat(bar);
          }

          // new candle -> append and keep size bounded
          const next = arr.concat(bar);
          return next.length > 500 ? next.slice(next.length - 500) : next;
        });

        setStatus({ ok: true, msg: "" });
      } catch {
        // ignore parse errors
      }
    };

    ws.onerror = () => {
      setStatus({ ok: false, msg: "Live stream error. Retrying…" });
    };

    return () => {
      try {
        ws.close();
      } catch {}
    };
  }, [symbol, safeInterval]);

  const last = data?.[data.length - 1];
  const lastPrice = last?.close;

  // Calculate price change
  const first = data?.[0];
  const priceChange = first?.close ? ((lastPrice - first.close) / first.close * 100) : 0;
  const isPositive = priceChange >= 0;

  const chartData = useMemo(() => {
    return (Array.isArray(data) ? data : []).map((d) => ({
      t: d.t,
      close: d.close,
    }));
  }, [data]);

  return (
    <div
      className="h-full w-full rounded-xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 overflow-hidden shadow-xl shadow-black/20"
      style={height ? { height } : undefined}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-800/80 flex items-center justify-between bg-gray-900/40">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary-500/20 to-cyan-500/20 flex items-center justify-center border border-primary-500/20">
            <span className="text-sm font-bold text-white">{symbol.substring(0, 2)}</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              {symbol}
              <span className="text-xs font-normal text-gray-400">
                / USDT
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {safeInterval.toUpperCase()} • Live
            </div>
          </div>
        </div>

        <div className="text-right">
          <div className="text-lg font-bold text-white tabular-nums">
            {Number.isFinite(lastPrice) ? lastPrice.toFixed(2) : "—"}
          </div>
          {Number.isFinite(priceChange) && (
            <div className={`text-xs font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="h-[calc(100%-60px)] w-full bg-gradient-to-b from-gray-900/50 to-gray-950/50">
        {chartData.length === 0 ? (
          <div className="h-full w-full grid place-items-center text-sm text-gray-500">
            {status.ok ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                <span>Loading chart data...</span>
              </div>
            ) : (
              <div className="text-amber-400/70">{status.msg || "Unable to load chart"}</div>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 16, right: 20, bottom: 12, left: 8 }}>
              <defs>
                <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                  <stop offset="50%" stopColor="#8B5CF6" stopOpacity={0.05} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#06B6D4" />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="t"
                tickFormatter={fmtTime}
                minTickGap={40}
                stroke="rgba(255,255,255,0.12)"
                tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={["auto", "auto"]}
                stroke="rgba(255,255,255,0.12)"
                tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                width={60}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                labelFormatter={(v) => new Date(v).toLocaleString()}
                formatter={(v) => [`$${Number(v).toFixed(2)}`, "Price"]}
                contentStyle={{
                  background: "rgba(17, 24, 39, 0.95)",
                  border: "1px solid rgba(55, 65, 81, 0.6)",
                  borderRadius: 12,
                  color: "white",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                  backdropFilter: "blur(12px)",
                  padding: "12px 16px",
                }}
                labelStyle={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "11px",
                  fontWeight: 500,
                }}
                itemStyle={{
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
                cursor={{
                  stroke: "rgba(255,255,255,0.15)",
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />
              <Line
                type="monotone"
                dataKey="close"
                dot={false}
                stroke="url(#lineGradient)"
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
